import React, { useEffect, useState } from 'react';
import {
  X, User as UserIcon, Mail, Phone, MapPin, Building2, IdCard, Hash,
  Briefcase, Wifi, WifiOff, LocateFixed, Clock, Loader2, ShieldCheck,
} from 'lucide-react';
import { searchWorkers, getWorkplaces, searchCompanies, getHourBalance } from '../services/api';
import { USER_TYPE_LABELS } from '../lib/permissions';
import { computationStartKey } from '../lib/hourBalance';

// Modal de perfil del usuario actual. Refetch del worker al abrir para
// que los datos estén frescos (el AuthContext solo guarda un subconjunto
// de campos en buildUser — phone, nif, ssNumber, createdAt no estan).
const ProfileModal = ({ open, user, onClose }) => {
  const [worker, setWorker] = useState(null);
  const [company, setCompany] = useState(null);
  const [workplace, setWorkplace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fecha de inicio de cómputo ("Trabaja desde"): no es un campo de Person,
  // sale de la fila marcadora de /api/hourBalance (src/lib/hourBalance.js).
  const [computationStart, setComputationStart] = useState(null);

  useEffect(() => {
    if (!open || !user?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setWorker(null);
    setCompany(null);
    setWorkplace(null);
    setComputationStart(null);
    // Best-effort: si falla, simplemente no se muestra la fila.
    getHourBalance(user.id)
      .then((list) => { if (!cancelled) setComputationStart(computationStartKey(list)); })
      .catch(() => {});
    (async () => {
      try {
        // 1) Worker completo (mismo patron que AuthContext.hydrateFromToken).
        const workers = await searchWorkers('');
        if (cancelled) return;
        const me = Array.isArray(workers)
          ? workers.find((w) => w.id === user.id) || null
          : null;
        setWorker(me);

        // 2) Company y workplace en paralelo si hay companyId.
        if (me?.companyId) {
          const companyId = Number(me.companyId);
          const [companies, workplaces] = await Promise.all([
            searchCompanies('').catch(() => []),
            getWorkplaces(companyId).catch(() => []),
          ]);
          if (cancelled) return;
          setCompany((Array.isArray(companies) ? companies : []).find((c) => Number(c.id) === companyId) || null);
          if (me.workingSite != null) {
            setWorkplace((Array.isArray(workplaces) ? workplaces : []).find(
              (w) => Number(w.id) === Number(me.workingSite)
            ) || null);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user?.id]);

  if (!open) return null;

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('es-ES', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch { return iso; }
  };

  // Clave de día ('YYYY-MM-DD'): se formatea en UTC para que el navegador no
  // la desplace al día anterior.
  const fmtDayKeyEs = (key) => {
    if (!key) return null;
    try {
      return new Date(`${key}T00:00:00Z`).toLocaleDateString('es-ES', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
      });
    } catch { return key; }
  };

  // El worker resuelto tiene prioridad sobre el user del context (mas completo).
  const w = worker || {};
  const name = w.name || user?.nombre || user?.email || 'Usuario';
  const email = w.email || user?.email || '—';
  const phone = w.phone || null;
  const nif = w.nif || null;
  const ssNumber = w.ssNumber || null;
  const roleLabel = USER_TYPE_LABELS[w.type ?? user?.type] || 'Trabajador';
  const province = w.province || user?.province || null;
  const locality = w.locality || user?.locality || null;
  const community = w.autonomousCommunity || user?.autonomousCommunity || null;
  const initial = (name || 'U').charAt(0).toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Mi perfil</div>
            <h2 className="font-display text-lg font-bold text-ink">Tus datos</h2>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-md transition" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* Cabecera con avatar + identificacion */}
          <div className="px-6 py-5 bg-gradient-to-br from-primary-soft to-white border-b border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-bold text-2xl shadow-lg shadow-primary/30">
                {initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl font-bold text-ink truncate">{name}</div>
                <div className="text-sm text-primary-darker font-semibold flex items-center gap-1.5 mt-0.5">
                  <ShieldCheck size={13} /> {roleLabel}
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-ink-muted">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Cargando datos…</span>
            </div>
          ) : error ? (
            <div className="px-6 py-4 text-sm text-danger">No se pudieron cargar todos los datos: {error.message || 'error desconocido'}.</div>
          ) : (
            <div className="px-6 py-5 space-y-6">
              {/* Datos personales */}
              <Section title="Datos personales" icon={UserIcon}>
                <Row icon={Mail} label="Email">
                  {email !== '—' ? (
                    <a href={`mailto:${email}`} className="text-primary-darker hover:underline">{email}</a>
                  ) : <Empty />}
                </Row>
                <Row icon={Phone} label="Teléfono">
                  {phone ? (
                    <a href={`tel:${phone}`} className="text-primary-darker hover:underline tabular-nums">{phone}</a>
                  ) : <Empty />}
                </Row>
                <Row icon={IdCard} label="NIF / DNI">
                  {nif ? <span className="tabular-nums">{nif}</span> : <Empty />}
                </Row>
                <Row icon={Hash} label="Núm. Seguridad Social">
                  {ssNumber ? <span className="tabular-nums">{ssNumber}</span> : <Empty />}
                </Row>
              </Section>

              {/* Trabajo */}
              <Section title="Trabajo" icon={Briefcase}>
                <Row icon={Building2} label="Empresa">
                  {company?.name || (w.companyId ? `Empresa #${w.companyId}` : <Empty />)}
                </Row>
                <Row icon={MapPin} label="Sede">
                  {workplace?.name || (w.workingSite != null ? `Sede #${w.workingSite}` : <Empty />)}
                </Row>
                <Row icon={MapPin} label="Provincia">{province || <Empty />}</Row>
                <Row icon={MapPin} label="Localidad">{locality || <Empty />}</Row>
                <Row icon={MapPin} label="Comunidad autónoma">{community || <Empty />}</Row>
              </Section>

              {/* Configuracion de fichaje */}
              <Section title="Configuración de fichaje" icon={Clock}>
                <Row icon={LocateFixed} label="GPS requerido">
                  <Bool value={w.geoTracking} />
                </Row>
                <Row icon={w.worksRemotely ? Wifi : WifiOff} label="Modalidad">
                  {w.worksRemotely == null ? <Empty /> : (w.worksRemotely ? 'Remoto' : 'Presencial')}
                </Row>
                <Row icon={Clock} label="Permite horas extra">
                  <Bool value={w.overtimeAllowed} />
                </Row>
                <Row icon={Clock} label="Bloqueo entrada antes de hora">
                  <Bool value={w.enforceStartHour} />
                </Row>
                <Row icon={Clock} label="Solicitud de fichaje diferido">
                  <Bool value={w.canRequestCheckins} />
                </Row>
              </Section>

              {/* Cuenta */}
              {(w.createdAt || w.updatedAt || computationStart) && (
                <Section title="Cuenta" icon={ShieldCheck}>
                  {w.createdAt && (
                    <Row label="Cuenta creada">{fmtDate(w.createdAt)}</Row>
                  )}
                  {computationStart && (
                    <Row label="Trabaja desde">
                      {fmtDayKeyEs(computationStart)}
                      <span className="text-ink-muted text-xs ml-2">Tus horas cuentan desde esta fecha.</span>
                    </Row>
                  )}
                  {w.updatedAt && (
                    <Row label="Última actualización">{fmtDate(w.updatedAt)}</Row>
                  )}
                </Section>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-between items-center gap-3">
          <div className="text-xs text-ink-muted">
            Para modificar tus datos, contacta con el administrador de tu empresa.
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-white bg-ink hover:bg-ink/90 rounded-lg transition"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 flex items-center gap-1.5">
      <Icon size={11} /> {title}
    </div>
    <dl className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
      {children}
    </dl>
  </div>
);

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50/50 transition-colors">
    <div className="w-32 shrink-0 text-xs text-ink-muted font-medium flex items-center gap-1.5">
      {Icon && <Icon size={11} className="text-ink-muted/70" />}
      {label}
    </div>
    <dd className="flex-1 text-sm text-ink min-w-0 truncate">{children}</dd>
  </div>
);

const Empty = () => <span className="text-ink-muted/60 italic">No registrado</span>;

const Bool = ({ value }) => {
  if (value == null) return <Empty />;
  return value ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-xs font-semibold">Sí</span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-ink-muted text-xs font-semibold">No</span>
  );
};

export default ProfileModal;
